import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { 
  Search, User, Mail, Phone, Calendar, Star, 
  Plus, MoreVertical, Clock, Loader2, Edit, Trash2
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminStaff = () => {
  const { toast } = useToast();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({ totalStaff: 0, providers: 0, totalAppointments: 0, avgRating: 0 });

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const practiceId = localStorage.getItem('practiceId') || 'demo-practice-001';
      
      const response = await fetch(
        `${BACKEND_URL}/api/staff?practiceId=${practiceId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          setStaff(data);
          calculateStats(data);
        } else {
          // Use mock data if no staff in DB
          setStaff(mockStaff);
          calculateStats(mockStaff);
        }
      } else {
        setStaff(mockStaff);
        calculateStats(mockStaff);
      }
    } catch (err) {
      console.error('Error fetching staff:', err);
      setStaff(mockStaff);
      calculateStats(mockStaff);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (staffList) => {
    const providers = staffList.filter(s => s.appointmentsThisMonth !== null);
    setStats({
      totalStaff: staffList.length,
      providers: providers.length,
      totalAppointments: staffList.reduce((sum, s) => sum + (s.appointmentsThisMonth || 0), 0),
      avgRating: (staffList.filter(s => s.rating).reduce((sum, s) => sum + s.rating, 0) / Math.max(staffList.filter(s => s.rating).length, 1)).toFixed(1)
    });
  };

  const mockStaff = [
    {
      id: 's1',
      name: 'Dr. Emily Chen',
      role: 'Medical Director',
      email: 'emily.chen@mdspa360.com',
      phone: '+1 (555) 100-0001',
      image: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=200',
      specialties: ['Injectables', 'Facial Aesthetics', 'Laser Treatments'],
      availability: ['Monday', 'Tuesday', 'Wednesday', 'Friday'],
      rating: 4.9,
      appointmentsThisMonth: 145,
      revenue: 32500,
      status: 'active',
      hireDate: '2022-03-15'
    },
    {
      id: 's2',
      name: 'Dr. Michael Torres',
      role: 'Aesthetic Physician',
      email: 'michael.torres@mdspa360.com',
      phone: '+1 (555) 100-0002',
      image: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=200',
      specialties: ['Laser Treatments', 'Skin Rejuvenation', 'Body Contouring'],
      availability: ['Tuesday', 'Thursday', 'Friday', 'Saturday'],
      rating: 4.8,
      appointmentsThisMonth: 132,
      revenue: 28750,
      status: 'active',
      hireDate: '2022-06-01'
    },
    {
      id: 's3',
      name: 'Sarah Kim, RN',
      role: 'Nurse Injector',
      email: 'sarah.kim@mdspa360.com',
      phone: '+1 (555) 100-0003',
      image: 'https://images.unsplash.com/photo-1594824476967-48c8b964273f?w=200',
      specialties: ['Botox', 'Fillers', 'Microneedling'],
      availability: ['Monday', 'Wednesday', 'Thursday', 'Friday'],
      rating: 4.7,
      appointmentsThisMonth: 65,
      revenue: 10000,
      status: 'active',
      hireDate: '2023-01-10'
    },
    {
      id: 's4',
      name: 'Jessica Martinez',
      role: 'Aesthetician',
      email: 'jessica.m@mdspa360.com',
      phone: '+1 (555) 100-0004',
      image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200',
      specialties: ['Facials', 'Chemical Peels', 'Hydrafacial'],
      availability: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      rating: 4.9,
      appointmentsThisMonth: 98,
      revenue: 8500,
      status: 'active',
      hireDate: '2023-04-20'
    },
    {
      id: 's5',
      name: 'Amanda Wilson',
      role: 'Front Desk Manager',
      email: 'amanda.w@mdspa360.com',
      phone: '+1 (555) 100-0005',
      image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=200',
      specialties: ['Patient Relations', 'Scheduling', 'Billing'],
      availability: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      rating: null,
      appointmentsThisMonth: null,
      revenue: null,
      status: 'active',
      hireDate: '2022-08-15'
    }
  ];

  const filteredStaff = staff.filter(member =>
    member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.specialties.some(s => s.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading staff...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-staff">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Staff Directory</h1>
          <p className="text-gray-600">Manage your team members and providers</p>
        </div>
        <Button data-testid="add-staff-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Staff Member
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <User className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalStaff}</p>
                <p className="text-sm text-gray-600">Total Staff</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/20 rounded-lg">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.providers}</p>
                <p className="text-sm text-gray-600">Providers</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Calendar className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalAppointments}</p>
                <p className="text-sm text-gray-600">Appointments/Month</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Star className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.avgRating}</p>
                <p className="text-sm text-gray-600">Avg Rating</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search staff by name, role, or specialty..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="staff-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Staff Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredStaff.map((member) => (
          <Card key={member.id} className="overflow-hidden" data-testid={`staff-card-${member.id}`}>
            <div className="relative">
              <img
                src={member.image}
                alt={member.name}
                className="w-full h-48 object-cover"
              />
              <Badge 
                className="absolute top-3 right-3"
                variant={member.status === 'active' ? 'default' : 'secondary'}
              >
                {member.status}
              </Badge>
            </div>
            
            <CardContent className="pt-4">
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">{member.name}</h3>
                <p className="text-sm text-primary">{member.role}</p>
                {member.rating && (
                  <div className="flex items-center justify-center gap-1 mt-1">
                    <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                    <span className="text-sm font-medium">{member.rating}</span>
                  </div>
                )}
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Mail className="h-4 w-4" />
                  <span className="truncate">{member.email}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Phone className="h-4 w-4" />
                  <span>{member.phone}</span>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-xs text-gray-500 mb-2">Specialties</p>
                <div className="flex flex-wrap gap-1">
                  {member.specialties.slice(0, 3).map((specialty, i) => (
                    <Badge key={i} variant="secondary" className="text-xs">
                      {specialty}
                    </Badge>
                  ))}
                  {member.specialties.length > 3 && (
                    <Badge variant="secondary" className="text-xs">
                      +{member.specialties.length - 3}
                    </Badge>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <p className="text-xs text-gray-500 mb-2">Availability</p>
                <div className="flex flex-wrap gap-1">
                  {member.availability.map((day, i) => (
                    <span key={i} className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                      {day.slice(0, 3)}
                    </span>
                  ))}
                </div>
              </div>

              {member.appointmentsThisMonth !== null && (
                <div className="grid grid-cols-2 gap-2 pt-3 border-t">
                  <div className="text-center">
                    <p className="text-lg font-semibold">{member.appointmentsThisMonth}</p>
                    <p className="text-xs text-gray-500">Appointments</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-semibold">${(member.revenue / 1000).toFixed(1)}k</p>
                    <p className="text-xs text-gray-500">Revenue</p>
                  </div>
                </div>
              )}

              <div className="flex gap-2 mt-4">
                <Button variant="outline" size="sm" className="flex-1">
                  <Edit className="h-3 w-3 mr-1" />
                  Edit
                </Button>
                <Button variant="outline" size="sm" className="flex-1">
                  <Calendar className="h-3 w-3 mr-1" />
                  Schedule
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredStaff.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <User className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No staff members found matching your search.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminStaff;
